import {
  DENSITY_MAXIMUM_C,
  GROUP16_HYDRIDES,
  ICE_DENSITY_0C,
  LIQUID_WATER_DENSITY,
  SPECIFIC_HEATS,
  VAPORISATION_ENTHALPIES,
  densestLiquidPoint,
  type WaterVisual,
} from './waterChemistry';
import {
  QUALITY_GUIDELINES,
  formatGuideline,
  formatParameterValue,
  exceedsGuideline,
  type QualityParameter,
} from './waterChemistryQuality';

const PERCENT = 100;

interface BarRow {
  id: string;
  label: string;
  value: number;
  valueLabel: string;
  tag?: string;
  highlight: boolean;
}

function HorizontalBars({ rows, max, label, caption }: { rows: BarRow[]; max: number; label: string; caption: string }) {
  return (
    <figure className="min-w-0" aria-label={label}>
      <ul className="grid gap-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="grid grid-cols-[6.5rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-xs font-bold text-ink-soft sm:grid-cols-[8rem_minmax(0,1fr)_5.5rem]"
            aria-label={`${row.label}: ${row.valueLabel}${row.tag ? `, ${row.tag}` : ''}`}
          >
            <span className={`truncate ${row.highlight ? 'font-extrabold text-ink' : ''}`}>{row.label}</span>
            <span className="relative h-5 rounded-full bg-surface" aria-hidden="true">
              <span
                className={`absolute inset-y-0.5 left-0 rounded-full ${row.highlight ? 'bg-brand-coral' : 'bg-brand-sky'}`}
                style={{ width: `${Math.max((row.value / max) * PERCENT, 1)}%` }}
              />
            </span>
            <span className="text-right">
              {row.valueLabel}
              {row.tag ? (
                <span className="block text-[0.6rem] font-extrabold uppercase text-slate2">{row.tag}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <figcaption className="mt-3 text-xs font-medium leading-5 text-slate2">{caption}</figcaption>
    </figure>
  );
}

function SpecificHeatChart() {
  const max = Math.max(...SPECIFIC_HEATS.map((entry) => entry.value));
  return (
    <HorizontalBars
      label="Specific heat capacities near 25 °C"
      caption="Specific heat capacity, J g⁻¹ K⁻¹, near 25 °C. Water is highlighted."
      max={max}
      rows={SPECIFIC_HEATS.map((entry) => ({
        id: entry.id,
        label: entry.name,
        value: entry.value,
        valueLabel: `${entry.value}`,
        highlight: entry.id === 'water',
      }))}
    />
  );
}

function VaporisationChart() {
  const max = Math.max(...VAPORISATION_ENTHALPIES.map((entry) => entry.enthalpyKJ));
  return (
    <HorizontalBars
      label="Enthalpies of vaporisation at the normal boiling point"
      caption="ΔvapH, kJ mol⁻¹, at each normal boiling point. “H-bond” marks substances whose molecules hydrogen bond."
      max={max}
      rows={VAPORISATION_ENTHALPIES.map((entry) => ({
        id: entry.id,
        label: `${entry.formula} ${entry.name}`,
        value: entry.enthalpyKJ,
        valueLabel: `${entry.enthalpyKJ}`,
        tag: entry.hydrogenBonding ? 'H-bond' : 'no H-bond',
        highlight: entry.id === 'water',
      }))}
    />
  );
}

const HYDRIDE_SCALE_MIN_C = -100;
const HYDRIDE_SCALE_MAX_C = 120;

function HydrideChart() {
  const span = HYDRIDE_SCALE_MAX_C - HYDRIDE_SCALE_MIN_C;
  const percent = (celsius: number) => ((celsius - HYDRIDE_SCALE_MIN_C) / span) * PERCENT;
  const zero = percent(0);
  return (
    <figure className="min-w-0" aria-label="Boiling points of the group 16 hydrides">
      <ul className="grid gap-2">
        {GROUP16_HYDRIDES.map((entry) => {
          const position = percent(entry.boilingPointC);
          const isWater = entry.formula === 'H₂O';
          return (
            <li
              key={entry.formula}
              className="grid grid-cols-[4.5rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-xs font-bold text-ink-soft"
              aria-label={`${entry.formula}, ${entry.molarMass} g/mol: boiling point ${entry.boilingPointC} °C`}
            >
              <span className={isWater ? 'font-extrabold text-ink' : ''}>{entry.formula}</span>
              <span className="relative h-5 rounded-full bg-surface" aria-hidden="true">
                <span className="absolute inset-y-0 w-px bg-ink" style={{ left: `${zero}%` }} />
                <span
                  className={`absolute inset-y-0.5 rounded-full ${isWater ? 'bg-brand-coral' : 'bg-brand-sky'}`}
                  style={{ left: `${Math.min(position, zero)}%`, width: `${Math.max(Math.abs(position - zero), 1)}%` }}
                />
              </span>
              <span className="text-right">
                {entry.boilingPointC} °C
                <span className="block text-[0.6rem] font-extrabold uppercase text-slate2">
                  {isWater ? 'H-bond' : `${entry.molarMass} g/mol`}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      <figcaption className="mt-3 text-xs font-medium leading-5 text-slate2">
        Normal boiling points at 101.325 kPa, ordered by molar mass. Bars start at 0 °C. Water breaks the trend.
      </figcaption>
    </figure>
  );
}

const DENSITY_VIEW = { width: 320, height: 190, left: 46, right: 12, top: 14, bottom: 34 } as const;
const DENSITY_MIN = 0.995;
const DENSITY_MAX = 1.0002;
const DENSITY_TICKS = [0.996, 0.998, 1];
const TEMPERATURE_MAX_C = 30;
const TEMPERATURE_TICKS = [0, 4, 10, 20, 30];

function DensityChart() {
  const plotWidth = DENSITY_VIEW.width - DENSITY_VIEW.left - DENSITY_VIEW.right;
  const plotHeight = DENSITY_VIEW.height - DENSITY_VIEW.top - DENSITY_VIEW.bottom;
  const x = (celsius: number) => DENSITY_VIEW.left + (celsius / TEMPERATURE_MAX_C) * plotWidth;
  const y = (density: number) =>
    DENSITY_VIEW.top + ((DENSITY_MAX - density) / (DENSITY_MAX - DENSITY_MIN)) * plotHeight;
  const peak = densestLiquidPoint();
  const path = LIQUID_WATER_DENSITY.map(
    (point, index) => `${index === 0 ? 'M' : 'L'}${x(point.temperatureC).toFixed(1)},${y(point.density).toFixed(1)}`,
  ).join(' ');
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${DENSITY_VIEW.width} ${DENSITY_VIEW.height}`}
        className="w-full"
        role="img"
        aria-label={`Density of liquid water from 0 to 30 °C. Maximum ${peak.density} g/mL at ${peak.temperatureC} °C. Ice at 0 °C is ${ICE_DENSITY_0C} g/mL, far below this scale.`}
      >
        {DENSITY_TICKS.map((tick) => (
          <g key={tick}>
            <line x1={DENSITY_VIEW.left} x2={DENSITY_VIEW.width - DENSITY_VIEW.right} y1={y(tick)} y2={y(tick)} stroke="currentColor" className="text-hairline" />
            <text x={DENSITY_VIEW.left - 4} y={y(tick) + 3} fontSize="8" textAnchor="end" fill="currentColor" className="text-slate2">
              {tick.toFixed(3)}
            </text>
          </g>
        ))}
        {TEMPERATURE_TICKS.map((tick) => (
          <text key={tick} x={x(tick)} y={DENSITY_VIEW.height - DENSITY_VIEW.bottom + 12} fontSize="8" textAnchor="middle" fill="currentColor" className="text-slate2">
            {tick}
          </text>
        ))}
        <text x={DENSITY_VIEW.left + plotWidth / 2} y={DENSITY_VIEW.height - 4} fontSize="9" fontWeight="700" textAnchor="middle" fill="currentColor" className="text-ink-soft">
          Temperature (°C)
        </text>
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2.5" className="text-brand-sky" />
        {LIQUID_WATER_DENSITY.map((point) => (
          <circle key={point.temperatureC} cx={x(point.temperatureC)} cy={y(point.density)} r="3" className="fill-brand-sky" />
        ))}
        <line x1={x(DENSITY_MAXIMUM_C)} x2={x(DENSITY_MAXIMUM_C)} y1={y(peak.density)} y2={DENSITY_VIEW.height - DENSITY_VIEW.bottom} stroke="currentColor" strokeDasharray="3 3" className="text-brand-coral" />
        <circle cx={x(peak.temperatureC)} cy={y(peak.density)} r="5" className="fill-brand-coral" />
        <text x={x(peak.temperatureC) + 8} y={y(peak.density) + 12} fontSize="9" fontWeight="700" fill="currentColor" className="text-ink">
          maximum: {peak.density} g/mL at about {DENSITY_MAXIMUM_C} °C
        </text>
      </svg>
      <figcaption className="mt-2 text-xs font-medium leading-5 text-slate2">
        Density of liquid water, g mL⁻¹, at 101.325 kPa. Ice at 0 °C is {ICE_DENSITY_0C} g mL⁻¹ — below
        the bottom of this scale, so it floats.
      </figcaption>
    </figure>
  );
}

export function WaterPropertyVisual({ visual }: { visual: WaterVisual }) {
  switch (visual) {
    case 'specific-heat':
      return <SpecificHeatChart />;
    case 'vaporisation':
      return <VaporisationChart />;
    case 'hydrides':
      return <HydrideChart />;
    case 'density':
      return <DensityChart />;
    default:
      return null;
  }
}

const PH_SCALE_MAX = 14;
/** A guideline maximum sits at this fraction of the bar, so exceedances stay visible. */
const GUIDELINE_FRACTION = 0.5;

function meterPosition(parameter: QualityParameter, value: number): { valueAt: number; bandFrom: number; bandTo: number } {
  const guideline = QUALITY_GUIDELINES[parameter];
  if (parameter === 'ph') {
    return {
      valueAt: (value / PH_SCALE_MAX) * PERCENT,
      bandFrom: ((guideline.min ?? 0) / PH_SCALE_MAX) * PERCENT,
      bandTo: (guideline.max / PH_SCALE_MAX) * PERCENT,
    };
  }
  const scaleMax = guideline.max / GUIDELINE_FRACTION;
  return {
    valueAt: Math.min(value / scaleMax, 1) * PERCENT,
    bandFrom: 0,
    bandTo: GUIDELINE_FRACTION * PERCENT,
  };
}

export function GuidelineMeter({ parameter, value }: { parameter: QualityParameter; value: number }) {
  const guideline = QUALITY_GUIDELINES[parameter];
  const fails = exceedsGuideline(parameter, value);
  const { valueAt, bandFrom, bandTo } = meterPosition(parameter, value);
  const status = fails ? `Outside ${guideline.kind} value` : 'Meets guideline';
  return (
    <li
      className="grid gap-1 rounded-2xl border border-hairline bg-white px-3 py-2"
      aria-label={`${guideline.label}: ${formatParameterValue(parameter, value)}; guideline ${formatGuideline(parameter)}; ${status}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs font-bold text-ink-soft">
        <span className="font-extrabold text-ink">{guideline.label}</span>
        <span>
          {formatParameterValue(parameter, value)} · guideline {formatGuideline(parameter)}
        </span>
      </div>
      <div className="relative h-4 rounded-full bg-surface" aria-hidden="true">
        <span
          className="absolute inset-y-0 rounded-full bg-wash-mint"
          style={{ left: `${bandFrom}%`, width: `${bandTo - bandFrom}%` }}
        />
        <span
          className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white ${fails ? 'bg-brand-coral' : 'bg-brand-mint'}`}
          style={{ left: `${valueAt}%` }}
        />
      </div>
      <p className={`text-[0.7rem] font-extrabold uppercase tracking-[0.08em] ${fails ? 'text-brand-coral' : 'text-ink-soft'}`}>
        {fails ? '✕ ' : '✓ '}
        {status} ({guideline.kind})
      </p>
    </li>
  );
}
